import {
  Component,
  EventEmitter,
  Output,
  Input,
  signal,
  computed,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { PackageService } from '../../../services/package.service';
import { LocationService } from '../../../services/location.service';
import { Package, LocationOption } from '../../../shared/types';
import { CustomDatePickerComponent } from '../../../shared/components/custom-date-picker/custom-date-picker.component';
import {
  CheckboxMultiDropdownComponent,
  CheckboxMultiDropdownOption,
} from '../../../shared/components/checkbox-multi-dropdown/checkbox-multi-dropdown.component';
import {
  CUSTOMER_PHONE_MAX_LENGTH,
  sanitizeCustomerPhoneInput,
  validateCustomerPhone,
} from '../../../shared/validators/customer-contact-field.rules';
import { FleetPriceEffective, FleetPricingType } from '../../models/fleet.model';
import { strictNextCycleStart } from '../../utils/fleet-billing-cycle.util';

export type FleetBillingCycleType = 'weekly' | 'biweekly' | 'monthly';
/** Stored fleet status; inactive fleets stay listed but don't redeem or get scheduled invoices. */
export type FleetStatus = 'active' | 'inactive';

export interface AddFleetFormData {
  fleetId?: string;
  status: FleetStatus;
  /** Edit only: status when the form opened, to confirm activation changes on save. */
  originalStatus?: FleetStatus;
  client: string;
  contactNumber: string;
  email: string;
  /** Charged price for `pricingType` (API `price_per_vehicle`). */
  pricePerVehicle: number | null;
  pricingType: FleetPricingType;
  /** Edit, per wash, after a wash: when a price change applies (API `price_effective`). */
  priceEffective?: FleetPriceEffective | null;
  /** Edit only: the fleet has a wash, so pricing type is locked and price changes are deferred. */
  hasRedemptions?: boolean;
  /** Edit only: price in force today, the baseline for "price changed". */
  currentPrice?: number | null;
  /** Edit only: price already scheduled for a later cycle. */
  pendingPrice?: number | null;
  pendingEffectiveFrom?: string | null;
  package: string;
  packageId?: string;
  /** Comma-joined labels of the selected locations, for display. */
  location: string;
  /** Selected location IDs (multi-select); sent as `location_ids` on create/update. */
  locationIds: string[];
  billingCycleType: FleetBillingCycleType;
  startDate: string;
  expiryMonths: number;
  /** Payment term in days; maps to API `payment_term_days` (7|15|30|45|60|90). */
  paymentTermDays: number;
  vehicleCount: number | null;
  avgWashesPerMonth: number | null;
  notificationRecipients: string;
}

/** Frequency dropdown options for the Billing Cycle field; value is the exact backend `billing_cycle_type` enum. */
export const BILLING_CYCLE_FREQUENCIES: readonly {
  label: string;
  value: FleetBillingCycleType;
}[] = [
  { label: 'Weekly', value: 'weekly' },
  { label: 'Every 14 days', value: 'biweekly' },
  { label: 'Monthly', value: 'monthly' },
];

/** Pricing Type radio options; value is the backend `pricing_type`. */
export const PRICING_TYPE_OPTIONS: readonly {
  label: string;
  value: FleetPricingType;
  hint: string;
}[] = [
  {
    label: 'Per Vehicle',
    value: 'per_vehicle',
    hint: 'A fixed price is charged for each vehicle once per billing cycle, regardless of the number of washes.',
  },
  { label: 'Per Wash', value: 'per_wash', hint: 'A fixed price is charged for each wash.' },
];

/** Duration dropdown options; value maps directly to the backend's `contract_duration_months` (6/12/24/36/60 only). */
export const EXPIRY_OPTIONS: readonly { label: string; value: number }[] = [
  { label: '6 months', value: 6 },
  { label: '1 year', value: 12 },
  { label: '2 years', value: 24 },
  { label: '3 years', value: 36 },
  { label: '5 years', value: 60 },
];

/** Payment term options; value maps to backend `payment_term_days`. */
export const PAYMENT_TERM_OPTIONS: readonly { label: string; value: number }[] = [
  { label: 'Net 7', value: 7 },
  { label: 'Net 15', value: 15 },
  { label: 'Net 30', value: 30 },
  { label: 'Net 45', value: 45 },
  { label: 'Net 60', value: 60 },
  { label: 'Net 90', value: 90 },
];

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

/** Fleet unlimited: monthly membership wash type and wash_count_period of "unlimited". */
export function isUnlimitedWashPackage(pkg: Package): boolean {
  const washTypes = Array.isArray(pkg.wash_types) ? pkg.wash_types : [];
  if (!washTypes.includes('monthly_membership')) {
    return false;
  }
  const period = String(pkg.wash_count_period || '')
    .trim()
    .toLowerCase();
  return period === 'unlimited';
}

/** Fleet single-wash: has a valid positive single_list_price. */
export function isSingleWashPackage(pkg: Package): boolean {
  const price = parseFloat(pkg.single_list_price);
  return !Number.isNaN(price) && price > 0;
}

/** True when the package is offered at every selected location. */
export function packageOfferedAtAllLocations(pkg: Package, locationIds: string[]): boolean {
  if (!locationIds.length) {
    return false;
  }
  const offered = new Set((pkg.locations || []).map(loc => String(loc.id)));
  return locationIds.every(id => offered.has(String(id)));
}

/** Fleet-eligible packages: active, unlimited or single-wash, non-family, at all selected sites. */
export function filterFleetEligiblePackages(packages: Package[], locationIds: string[]): Package[] {
  return packages.filter(
    pkg =>
      pkg.package_status === true &&
      !pkg.family_plan &&
      (isUnlimitedWashPackage(pkg) || isSingleWashPackage(pkg)) &&
      packageOfferedAtAllLocations(pkg, locationIds)
  );
}

@Component({
  selector: 'app-add-fleet-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatRadioModule,
    CustomDatePickerComponent,
    CheckboxMultiDropdownComponent,
  ],
  templateUrl: './add-fleet-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-fleet-modal.component.scss'],
})
export class AddFleetModalComponent implements OnInit {
  @Input() isEditMode = false;
  @Input() editFleetData: AddFleetFormData | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<AddFleetFormData>();

  constructor(
    private packageService: PackageService,
    private locationService: LocationService
  ) {}

  // Form data signals
  readonly formData = signal<AddFleetFormData>({
    fleetId: undefined,
    status: 'active',
    client: '',
    contactNumber: '',
    email: '',
    pricePerVehicle: null,
    pricingType: 'per_vehicle',
    priceEffective: null,
    package: '',
    packageId: undefined,
    location: '',
    locationIds: [],
    billingCycleType: 'monthly',
    startDate: todayIso(),
    expiryMonths: 12,
    paymentTermDays: 30,
    vehicleCount: null,
    avgWashesPerMonth: null,
    notificationRecipients: '',
  });

  // Validation signals
  readonly clientNameError = signal<string>('');
  readonly maxClientNameLength = 100;
  /** Digits-only US phone; same cap as Customers / Orders / Employees. */
  readonly contactPhoneMaxLength = CUSTOMER_PHONE_MAX_LENGTH;
  readonly contactNumberError = signal<string>('');
  readonly emailError = signal<string>('');
  readonly vehicleCountError = signal<string>('');
  readonly avgWashesError = signal<string>('');
  readonly maxWashesCount = 100;
  readonly pricePerVehicleError = signal<string>('');
  readonly maxPricePerVehicle = 10000;

  // Package data — raw list; dropdown uses `packages` computed (intersection filter)
  readonly allPackages = signal<Package[]>([]);
  readonly packages = computed(() =>
    filterFleetEligiblePackages(this.allPackages(), this.formData().locationIds)
  );
  readonly loadingPackages = signal<boolean>(false);
  readonly creatingFleet = signal<boolean>(false);

  // Location data
  readonly locations = signal<LocationOption[]>([]);
  readonly loadingLocations = signal<boolean>(false);
  /** Options shaped for `app-checkbox-multi-dropdown` (`id` / `name`). */
  readonly locationCheckboxOptions = computed<CheckboxMultiDropdownOption[]>(() =>
    this.locations().map(loc => ({ id: loc.value, name: loc.label }))
  );

  // Warning state
  readonly showZeroWarning = signal<boolean>(false);

  // Dropdown options
  readonly billingCycleFrequencies = BILLING_CYCLE_FREQUENCIES;
  readonly expiryOptions = EXPIRY_OPTIONS;
  readonly paymentTermOptions = PAYMENT_TERM_OPTIONS;
  readonly pricingTypeOptions = PRICING_TYPE_OPTIONS;

  /** Pricing type can't change once the fleet has a wash. */
  readonly pricingTypeLocked = computed(() => {
    // Read the signal before the plain isEditMode flag so the dependency is always tracked.
    const hasRedemptions = Boolean(this.formData().hasRedemptions);
    return this.isEditMode && hasRedemptions;
  });

  readonly pricingTypeHint = computed(
    () => PRICING_TYPE_OPTIONS.find(o => o.value === this.formData().pricingType)?.hint || ''
  );

  /**
   * Edit after a wash with a new price: the change is deferred (per vehicle) or needs the
   * next-wash / next-cycle choice (per wash). Re-entering today's or the pending price is not a change.
   */
  readonly deferredPriceChange = computed(() => {
    const data = this.formData();
    if (!this.pricingTypeLocked() || data.pricePerVehicle === null) {
      return false;
    }
    return data.pricePerVehicle !== data.currentPrice && data.pricePerVehicle !== data.pendingPrice;
  });

  /** Start of the next billing cycle, when a deferred price change takes effect. */
  readonly nextCycleStartDate = computed(() => {
    const data = this.formData();
    if (!data.startDate) return null;
    const iso = strictNextCycleStart({
      billingCycleType: data.billingCycleType,
      billingCycleAnchor: data.startDate,
      asOfDate: todayIso(),
    });
    return this.parseIsoDate(iso);
  });

  readonly pendingPriceHint = computed(() => {
    const data = this.formData();
    if (data.pendingPrice == null || !data.pendingEffectiveFrom || !this.isEditMode) {
      return '';
    }
    return `Price changes to $${data.pendingPrice} on ${this.formatHintDate(
      this.parseIsoDate(data.pendingEffectiveFrom)
    )}.`;
  });

  /** Placeholder for the package mat-select based on location / load state. */
  readonly packagePlaceholder = computed(() => {
    if (this.loadingPackages()) {
      return 'Loading packages...';
    }
    if (this.formData().locationIds.length === 0) {
      return 'Select locations first';
    }
    if (this.packages().length === 0) {
      return 'No common packages offered at selected locations';
    }
    return 'Select package';
  });

  ngOnInit(): void {
    this.loadPackages();

    // Load locations and then populate form data if in edit mode
    this.loadingLocations.set(true);
    this.locationService.getLocations().subscribe({
      next: response => {
        if (response.status && response.data) {
          const locationOptions = response.data
            .filter((location: any) => location.status)
            .map((location: any) => ({
              label: `${location.branch} - ${location.city}`,
              value: location.id,
            }));
          this.locations.set(locationOptions);

          // If in edit mode, populate form with existing data and find proper location labels
          if (this.isEditMode && this.editFleetData) {
            const selectedLabels = locationOptions
              .filter((loc: any) => this.editFleetData?.locationIds.includes(loc.value))
              .map((loc: any) => loc.label);
            this.formData.set(this.normalizeEditFleetData(this.editFleetData, selectedLabels));
          } else if (!this.isEditMode) {
            // New fleet: one location is preselected; with several, all of them are.
            this.onLocationIdsChange(locationOptions.map((loc: LocationOption) => loc.value));
          }
        }
        this.loadingLocations.set(false);
      },
      error: error => {
        console.error('Error loading locations:', error);
        this.loadingLocations.set(false);

        // If in edit mode and location loading failed, still populate form
        if (this.isEditMode && this.editFleetData) {
          this.formData.set(this.normalizeEditFleetData(this.editFleetData));
        }
      },
    });
  }

  /**
   * Digits-only contact number when hydrating edit mode.
   * API may return formatting or a leading US country code (`+1` / `1XXXXXXXXXX`);
   * strip that before the shared 10-digit sanitize so we do not corrupt the NANP number.
   */
  private normalizeEditFleetData(
    editData: AddFleetFormData,
    selectedLabels?: string[]
  ): AddFleetFormData {
    const allowed = new Set(BILLING_CYCLE_FREQUENCIES.map(f => f.value));
    const billingCycleType = allowed.has(editData.billingCycleType)
      ? editData.billingCycleType
      : 'monthly';
    return {
      ...editData,
      billingCycleType,
      contactNumber: this.normalizeEditContactNumber(editData.contactNumber),
      location: selectedLabels?.length ? selectedLabels.join(', ') : editData.location,
    };
  }

  private normalizeEditContactNumber(value: string | null | undefined): string {
    const digits = String(value ?? '').replace(/\D/g, '');
    const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    return sanitizeCustomerPhoneInput(national);
  }

  loadPackages(): void {
    this.loadingPackages.set(true);
    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        if (response.status && response.data) {
          this.allPackages.set(response.data);
          this.clearPackageIfNoLongerEligible();
        }
        this.loadingPackages.set(false);
      },
      error: error => {
        console.error('Error loading packages:', error);
        this.loadingPackages.set(false);
      },
    });
  }

  /** Drop the selected package when it is no longer in the eligible intersection. */
  private clearPackageIfNoLongerEligible(): void {
    const selectedId = this.formData().packageId;
    if (!selectedId) {
      return;
    }
    const stillEligible = this.packages().some(pkg => pkg.id === selectedId);
    if (!stillEligible) {
      this.updateFormData({ package: '', packageId: undefined });
    }
  }

  /** Display label for a package option, e.g. "Unlimited Ultra-$89.00/vehicle/month" or "Basic Wash-$15.00/vehicle/wash". */
  packageOptionLabel(pkg: Package): string {
    const monthlyPrice = parseFloat(pkg.monthly_list_price);
    const hasMonthlyPrice = Boolean(pkg.monthly_list_price) && !Number.isNaN(monthlyPrice);
    const singlePrice = parseFloat(pkg.single_list_price);
    const hasSinglePrice = Boolean(pkg.single_list_price) && !Number.isNaN(singlePrice);

    // A package priced on both bases is only actually billed monthly when it meets the same
    // wash_types/period contract isUnlimitedWashPackage uses for eligibility; otherwise its real
    // charging basis is per-wash, even though a monthly price happens to be present too.
    if (hasMonthlyPrice && (!hasSinglePrice || isUnlimitedWashPackage(pkg))) {
      return `${pkg.package_name}-$${monthlyPrice.toFixed(2)}/vehicle/month`;
    }
    if (hasSinglePrice) {
      return `${pkg.package_name}-$${singlePrice.toFixed(2)}/vehicle/wash`;
    }
    return pkg.package_name;
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    const data = this.formData();
    if (this.isFormValid()) {
      this.save.emit(data);
    }
  }

  /**
   * Keydown handler to prevent typing beyond 100 characters and show error
   */
  onClientKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (currentValue.length >= this.maxClientNameLength) {
      event.preventDefault();
      this.clientNameError.set(`Client name cannot exceed ${this.maxClientNameLength} characters`);
    }
  }

  onClientInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const { value } = target;

    // Only allow letters and spaces, no special characters
    const filteredValue = value.replace(/[^A-Za-z\s]/g, '');

    if (value !== filteredValue) {
      target.value = filteredValue;
    }

    if (filteredValue.length > this.maxClientNameLength) {
      this.clientNameError.set(`Client name cannot exceed ${this.maxClientNameLength} characters`);
      const truncatedValue = filteredValue.substring(0, this.maxClientNameLength);
      target.value = truncatedValue;
      this.updateFormData({ client: truncatedValue });
    } else if (filteredValue.length === this.maxClientNameLength) {
      this.clientNameError.set(`Client name cannot exceed ${this.maxClientNameLength} characters`);
      this.updateFormData({ client: filteredValue });
    } else {
      this.clientNameError.set('');
      this.updateFormData({ client: filteredValue });
    }
  }

  onContactNumberInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const sanitized = sanitizeCustomerPhoneInput(target.value);
    if (target.value !== sanitized) {
      target.value = sanitized;
    }
    this.updateFormData({ contactNumber: sanitized });
    // Same US/NANP rules as Order Request (PhoneValidator.usaPhone via validateCustomerPhone).
    this.contactNumberError.set(validateCustomerPhone(sanitized));
  }

  onEmailInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.updateFormData({ email: value });
    this.emailError.set(
      value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Enter a valid email address' : ''
    );
  }

  onNotificationRecipientsInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.updateFormData({ notificationRecipients: target.value });
  }

  /**
   * Keydown handler to prevent typing beyond 10000 for the price
   */
  onPriceKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const currentNumValue = parseInt(currentValue) || 0;
    if (currentNumValue >= this.maxPricePerVehicle) {
      if (!isNaN(parseInt(event.key))) {
        event.preventDefault();
        this.pricePerVehicleError.set(
          `Charged price cannot exceed ${this.maxPricePerVehicle.toLocaleString()}`
        );
      }
    }
  }

  onPriceInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value);

    if (!isNaN(value) && value > this.maxPricePerVehicle) {
      target.value = this.maxPricePerVehicle.toString();
      this.updateFormData({ pricePerVehicle: this.maxPricePerVehicle });
      this.pricePerVehicleError.set(
        `Charged price cannot exceed ${this.maxPricePerVehicle.toLocaleString()}`
      );
      this.checkForZeroValues();
      return;
    }

    if (
      !isNaN(value) &&
      value >= 0 &&
      Number.isInteger(value) &&
      value <= this.maxPricePerVehicle
    ) {
      this.updateFormData({ pricePerVehicle: value });
      this.pricePerVehicleError.set('');
      this.checkForZeroValues();
    } else if (target.value === '') {
      this.updateFormData({ pricePerVehicle: null });
      this.pricePerVehicleError.set('');
      this.showZeroWarning.set(false);
    } else if (value < 0) {
      target.value = '0';
      this.updateFormData({ pricePerVehicle: 0 });
      this.pricePerVehicleError.set('');
      this.checkForZeroValues();
    } else if (value > this.maxPricePerVehicle) {
      target.value = this.maxPricePerVehicle.toString();
      this.updateFormData({ pricePerVehicle: this.maxPricePerVehicle });
      this.pricePerVehicleError.set(
        `Charged price cannot exceed ${this.maxPricePerVehicle.toLocaleString()}`
      );
      this.checkForZeroValues();
    }
  }

  onVehicleCountInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);

    if (!isNaN(value) && value >= 0 && Number.isInteger(value)) {
      this.updateFormData({ vehicleCount: value });
      this.vehicleCountError.set('');
      this.checkForZeroValues();
    } else if (target.value === '') {
      this.updateFormData({ vehicleCount: null });
      this.vehicleCountError.set('');
      this.showZeroWarning.set(false);
    } else if (value < 0) {
      target.value = '0';
      this.updateFormData({ vehicleCount: 0 });
      this.vehicleCountError.set('');
      this.checkForZeroValues();
    }
  }

  /**
   * Keydown handler to prevent typing beyond 100 for average washes
   */
  onAvgWashesKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    const currentNumValue = parseInt(currentValue, 10) || 0;
    if (currentNumValue >= this.maxWashesCount && !Number.isNaN(parseInt(event.key, 10))) {
      event.preventDefault();
      this.avgWashesError.set(`Average washes cannot exceed ${this.maxWashesCount}`);
    }
  }

  onAvgWashesInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);

    if (!isNaN(value) && value > this.maxWashesCount) {
      target.value = this.maxWashesCount.toString();
      this.updateFormData({ avgWashesPerMonth: this.maxWashesCount });
      this.avgWashesError.set(`Average washes cannot exceed ${this.maxWashesCount}`);
      this.checkForZeroValues();
      return;
    }

    if (!isNaN(value) && value >= 0 && Number.isInteger(value) && value <= this.maxWashesCount) {
      this.updateFormData({ avgWashesPerMonth: value });
      this.avgWashesError.set('');
      this.checkForZeroValues();
    } else if (target.value === '') {
      this.updateFormData({ avgWashesPerMonth: null });
      this.avgWashesError.set('');
      this.showZeroWarning.set(false);
    } else if (value < 0) {
      target.value = '0';
      this.updateFormData({ avgWashesPerMonth: 0 });
      this.avgWashesError.set('');
      this.checkForZeroValues();
    } else if (value > this.maxWashesCount) {
      target.value = this.maxWashesCount.toString();
      this.updateFormData({ avgWashesPerMonth: this.maxWashesCount });
      this.avgWashesError.set(`Average washes cannot exceed ${this.maxWashesCount}`);
      this.checkForZeroValues();
    }
  }

  checkForZeroValues(): void {
    const data = this.formData();
    const hasZero = Boolean(
      data.pricePerVehicle === 0 || data.vehicleCount === 0 || data.avgWashesPerMonth === 0
    );
    this.showZeroWarning.set(hasZero);
  }

  onStartDateChange(date: Date | null): void {
    this.updateFormData({ startDate: date ? date.toISOString().split('T')[0] : '' });
  }

  /**
   * Parsed Date for the start-date picker's [ngModel] binding, memoized via computed() so the
   * same Date instance is returned until the underlying ISO string actually changes. Parsing a
   * fresh Date on every template check (as a plain method call would) makes NgModel see a
   * "changed" input on every change-detection pass — including the dev-mode checkNoChanges pass
   * — which throws ExpressionChangedAfterItHasBeenCheckedError on every keystroke in the form
   * and makes the modal feel stuck.
   */
  readonly parsedStartDate = computed(() => this.parseIsoDate(this.formData().startDate));

  private parseIsoDate(value: string | undefined): Date | null {
    if (!value) return null;
    // YYYY-MM-DD is a calendar date; parse as local noon to avoid UTC day-shift.
    const ymd = String(value).trim().split('T')[0];
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /** Date the first invoice will be raised: one full billing cycle after the start date. */
  readonly firstInvoiceDate = computed(() => {
    const data = this.formData();
    if (!data.startDate) return null;
    const start = this.parseIsoDate(data.startDate);
    if (!start) return null;
    const date = new Date(start);
    switch (data.billingCycleType) {
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'biweekly':
        date.setDate(date.getDate() + 14);
        break;
      case 'monthly':
      default:
        date.setMonth(date.getMonth() + 1);
        break;
    }
    return date;
  });

  /** Formats a Date as "Mar 1, 2027", matching FleetService's Expiry/Billing Cycle column formatting. */
  formatHintDate(date: Date | null): string {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  isFormValid(): boolean {
    const data = this.formData();
    if (
      this.clientNameError().length > 0 ||
      this.contactNumberError().length > 0 ||
      this.emailError().length > 0 ||
      this.vehicleCountError().length > 0 ||
      this.avgWashesError().length > 0 ||
      this.pricePerVehicleError().length > 0
    ) {
      return false;
    }

    const contactNumber = sanitizeCustomerPhoneInput(data.contactNumber || '');
    const email = (data.email || '').trim();
    const needsPriceTiming =
      data.pricingType === 'per_wash' && this.deferredPriceChange() && !data.priceEffective;
    if (needsPriceTiming) {
      return false;
    }

    return Boolean(
      data.client &&
      data.client.length <= this.maxClientNameLength &&
      contactNumber &&
      !validateCustomerPhone(contactNumber) &&
      email &&
      data.pricePerVehicle &&
      data.pricePerVehicle > 0 &&
      data.pricePerVehicle <= this.maxPricePerVehicle &&
      data.package &&
      data.billingCycleType &&
      data.startDate &&
      data.paymentTermDays &&
      data.vehicleCount &&
      data.vehicleCount > 0 &&
      data.locationIds.length > 0
    );
  }

  updateFormData(updates: Partial<AddFleetFormData>): void {
    this.formData.update(current => ({ ...current, ...updates }));
  }

  /** Apply location multi-select changes and drop an ineligible package if needed. */
  onLocationIdsChange(locationIds: string[]): void {
    const location = this.locations()
      .filter(loc => locationIds.includes(loc.value))
      .map(loc => loc.label)
      .join(', ');
    this.updateFormData({ locationIds, location });
    this.clearPackageIfNoLongerEligible();
  }

  onStatusToggle(active: boolean): void {
    this.updateFormData({ status: active ? 'active' : 'inactive' });
  }

  onPricingTypeChange(value: FleetPricingType): void {
    if (this.pricingTypeLocked()) return;
    this.updateFormData({ pricingType: value, priceEffective: null });
  }

  onPriceEffectiveChange(value: FleetPriceEffective): void {
    this.updateFormData({ priceEffective: value });
  }

  onBillingCycleChange(value: FleetBillingCycleType): void {
    this.updateFormData({ billingCycleType: value });
  }

  onPackageIdChange(packageId: string): void {
    const selectedPackage = this.packages().find(pkg => pkg.id === packageId);
    if (selectedPackage) {
      this.updateFormData({
        package: selectedPackage.package_name,
        packageId: selectedPackage.id,
      });
    }
  }

  onExpiryChange(expiryMonths: number): void {
    this.updateFormData({ expiryMonths: Number(expiryMonths) });
  }

  onPaymentTermChange(paymentTermDays: number): void {
    this.updateFormData({ paymentTermDays: Number(paymentTermDays) });
  }

  billingCycleLabel(value: FleetBillingCycleType): string {
    return this.billingCycleFrequencies.find(f => f.value === value)?.label || '';
  }

  trackByValue(_: number, item: { value: string | number }): string | number {
    return item.value;
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }
}
